"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";

interface HighlightItem {
  url: string;
  title?: string;
  description?: string;
  thumbs?: {
    sm_q?: string;
    xl?: string;
  };
}

interface TourHighlightProps {
  highlights: HighlightItem[];
  title: string;
}

export default function TourHighlight({
  highlights,
  title,
}: TourHighlightProps) {
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const GAP = 24;

  const scrollImages = (direction: 1 | -1) => {
    const carousel = carouselRef.current;
    if (!(carousel instanceof HTMLDivElement)) return;
    const card = carousel.children[0] as HTMLElement | undefined;
    const cardWidth = (card?.offsetWidth || 0) + GAP;
    const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

    if (direction === 1) {
      if (carousel.scrollLeft >= maxScrollLeft - 1) {
        carousel.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        carousel.scrollBy({ left: cardWidth, behavior: "smooth" });
      }
    } else {
      if (carousel.scrollLeft <= 1) {
        carousel.scrollTo({
          left: maxScrollLeft,
          behavior: "smooth",
        });
      } else {
        carousel.scrollBy({ left: -cardWidth, behavior: "smooth" });
      }
    }
  };

  useEffect(() => {
    const onScroll = () => {
      const carousel = carouselRef.current;
      if (!(carousel instanceof HTMLDivElement)) return;
      const card = carousel.children[0] as HTMLElement | undefined;
      const cardWidth = (card?.offsetWidth || 0) + GAP;
      setScrollPosition(Math.round(carousel.scrollLeft / cardWidth));
    };
    const ref = carouselRef.current;
    if (ref instanceof HTMLDivElement) {
      ref.addEventListener("scroll", onScroll);
    }
    return () => {
      if (ref instanceof HTMLDivElement) {
        ref.removeEventListener("scroll", onScroll);
      }
    };
  }, []);

  const [showArrows, setShowArrows] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const carousel = carouselRef.current;
      if (!(carousel instanceof HTMLDivElement)) return;
      const hasOverflow = carousel.scrollWidth > carousel.clientWidth;
      setShowArrows(hasOverflow);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [highlights.length]);
  const justifyClass = highlights.length < 3 ? "justify-center" : "";

  return (
    <div className='w-full mt-16 mb-10 text-center relative'>
      <h3 className='font-kalam font-bold text-5xl mb-10'>{title}</h3>

      {showArrows && (
        <button
          onClick={() => scrollImages(-1)}
          className='absolute -left-15 top-1/2 transform -translate-y-1/2 hidden md:flex w-10 h-10 bg-[#A3A3A3] hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition z-50'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <polyline points='15 18 9 12 15 6' />
          </svg>
        </button>
      )}

      {showArrows && (
        <button
          onClick={() => scrollImages(1)}
          className='absolute -right-15 top-1/2 transform -translate-y-1/2 hidden md:flex w-10 h-10 bg-[#A3A3A3] hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition z-50'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <polyline points='9 18 15 12 9 6' />
          </svg>
        </button>
      )}

      <div
        ref={carouselRef}
        className={`flex gap-6 overflow-x-auto scroll-smooth no-scrollbar px-4 ${justifyClass}`}
      >
        {highlights.map((highlight: HighlightItem, index: number) => (
          <div
            key={index}
            className='shrink-0 w-full sm:w-[calc((100%-24px)/2)] lg:w-[calc((100%-48px)/3)]'
          >
            <div className='relative overflow-hidden rounded-lg h-50'>
              <div
                className='absolute inset-0 w-full h-full'
                style={{
                  WebkitMaskImage: "url(/assets/vylet2.svg)",
                  WebkitMaskSize: "contain",
                  WebkitMaskPosition: "center",
                  WebkitMaskRepeat: "no-repeat",
                  maskImage: "url(/assets/vylet2.svg)",
                  maskSize: "contain",
                  maskPosition: "center",
                  maskRepeat: "no-repeat",
                }}
              >
                <Image
                  src={highlight.thumbs?.sm_q ?? highlight.url}
                  alt={highlight.title || "Highlight Image"}
                  width={300}
                  height={200}
                  className='w-full h-full object-cover'
                  sizes='(max-width: 768px) 100vw, 300px'
                />
              </div>
            </div>
            <h4 className='mt-2 font-bold text-[#00638A] text-xl text-center'>
              {highlight.title || "Bez názvu"}
            </h4>
            <p className='text-sm text-center text-[15px] px-4 mt-1'>
              {highlight.description || "Bez popisu"}
            </p>
          </div>
        ))}
      </div>

      <div className='flex justify-center mt-4 md:hidden'>
        {highlights.map((_: HighlightItem, idx: number) => (
          <div
            key={idx}
            className={`w-2.5 h-2.5 mx-1 rounded-full transition ${
              scrollPosition === idx
                ? "bg-gray-600 w-3 h-3"
                : "bg-gray-400 opacity-50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
